import React, { useState } from 'react';
import { Button } from '../components/atoms/Button';
import { Input } from '../components/atoms/Input';
import { Checkbox } from '../components/atoms/Checkbox';
import { Badge } from '../components/atoms/Badge';
import { Spinner } from '../components/atoms/Spinner';
import { Skeleton } from '../components/atoms/Skeleton';
import { ErrorMessage } from '../components/atoms/ErrorMessage';

export const ComponentTestPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleClick = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 2000);
  };

  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1
        style={{ marginBottom: '32px', fontSize: '32px', fontWeight: 'bold' }}
      >
        Component Test Page
      </h1>

      {/* Button Tests */}
      <section style={{ marginBottom: '48px' }}>
        <h2
          style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}
        >
          Button Component
        </h2>

        {/* Variants */}
        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Variants
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="ghost">Ghost</Button>
          </div>
        </div>

        {/* Sizes */}
        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Sizes
          </h3>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button size="sm">Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg">Large</Button>
          </div>
        </div>

        {/* With Icons */}
        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            With Icons
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Button
              leftIcon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M8 0a1 1 0 011 1v6h6a1 1 0 110 2H9v6a1 1 0 11-2 0V9H1a1 1 0 010-2h6V1a1 1 0 011-1z" />
                </svg>
              }
            >
              Add New
            </Button>
            <Button
              variant="secondary"
              rightIcon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M5.828 10.172a.5.5 0 00-.707 0l-4.096 4.096L0 16h1.732l4.096-4.096a.5.5 0 000-.707z" />
                </svg>
              }
            >
              Edit
            </Button>
          </div>
        </div>

        {/* States */}
        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            States
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Button disabled>Disabled</Button>
            <Button isLoading={isLoading} onClick={handleClick}>
              {isLoading ? 'Loading...' : 'Click to Load'}
            </Button>
          </div>
        </div>

        {/* Full Width */}
        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Full Width
          </h3>
          <Button fullWidth>Full Width Button</Button>
        </div>

        {/* All Combinations */}
        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Interactive Examples
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Button onClick={() => alert('Primary clicked!')}>Click Me</Button>
            <Button
              variant="secondary"
              onClick={() => console.log('Secondary clicked')}
            >
              Console Log
            </Button>
            <Button variant="danger" onClick={() => confirm('Are you sure?')}>
              Confirm Delete
            </Button>
          </div>
        </div>
      </section>

      {/* Input Tests */}
      <section style={{ marginBottom: '48px' }}>
        <h2
          style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}
        >
          Input Component
        </h2>

        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Sizes
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              maxWidth: '400px',
            }}
          >
            <Input size="sm" placeholder="Small input" />
            <Input size="md" placeholder="Medium input (default)" />
            <Input size="lg" placeholder="Large input" />
          </div>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            States
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              maxWidth: '400px',
            }}
          >
            <Input
              placeholder="Normal input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <Input placeholder="Disabled input" disabled />
            <Input placeholder="Error state" error />
            <Input placeholder="Full width" fullWidth />
          </div>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Types
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              maxWidth: '400px',
            }}
          >
            <Input type="text" placeholder="Text input" />
            <Input type="email" placeholder="Email input" />
            <Input type="password" placeholder="Password input" />
            <Input type="number" placeholder="Number input" />
          </div>
        </div>
      </section>

      {/* Checkbox Tests */}
      <section style={{ marginBottom: '48px' }}>
        <h2
          style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}
        >
          Checkbox Component
        </h2>

        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            States
          </h3>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            <Checkbox
              label="Normal checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
            />
            <Checkbox label="Checked checkbox" checked />
            <Checkbox
              label="Indeterminate checkbox"
              indeterminate={isIndeterminate}
              onChange={() => setIsIndeterminate(!isIndeterminate)}
            />
            <Checkbox label="Disabled checkbox" disabled />
            <Checkbox label="Disabled checked" checked disabled />
          </div>
        </div>
      </section>

      {/* Badge Tests */}
      <section style={{ marginBottom: '48px' }}>
        <h2
          style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}
        >
          Badge Component
        </h2>

        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Status Variants
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Badge variant="draft">Draft</Badge>
            <Badge variant="published">Published</Badge>
            <Badge variant="archived">Archived</Badge>
          </div>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Semantic Variants
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Badge variant="success">Success</Badge>
            <Badge variant="warning">Warning</Badge>
            <Badge variant="danger">Danger</Badge>
            <Badge variant="info">Info</Badge>
            <Badge variant="neutral">Neutral</Badge>
          </div>
        </div>
      </section>

      {/* Spinner Tests */}
      <section style={{ marginBottom: '48px' }}>
        <h2
          style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}
        >
          Spinner Component
        </h2>

        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Sizes
          </h3>
          <div
            style={{
              display: 'flex',
              gap: '24px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <Spinner size="sm" />
              <p
                style={{ fontSize: '12px', marginTop: '8px', color: '#6b7280' }}
              >
                Small
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Spinner size="md" />
              <p
                style={{ fontSize: '12px', marginTop: '8px', color: '#6b7280' }}
              >
                Medium
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Spinner size="lg" />
              <p
                style={{ fontSize: '12px', marginTop: '8px', color: '#6b7280' }}
              >
                Large
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Skeleton Tests */}
      <section style={{ marginBottom: '48px' }}>
        <h2
          style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}
        >
          Skeleton Component
        </h2>

        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Variants
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              maxWidth: '600px',
            }}
          >
            <div>
              <p
                style={{
                  fontSize: '14px',
                  marginBottom: '8px',
                  color: '#6b7280',
                }}
              >
                Text (default):
              </p>
              <Skeleton variant="text" />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
            </div>
            <div>
              <p
                style={{
                  fontSize: '14px',
                  marginBottom: '8px',
                  color: '#6b7280',
                }}
              >
                Circular:
              </p>
              <Skeleton variant="circular" />
            </div>
            <div>
              <p
                style={{
                  fontSize: '14px',
                  marginBottom: '8px',
                  color: '#6b7280',
                }}
              >
                Rectangular:
              </p>
              <Skeleton variant="rectangular" />
            </div>
            <div>
              <p
                style={{
                  fontSize: '14px',
                  marginBottom: '8px',
                  color: '#6b7280',
                }}
              >
                Custom sizes:
              </p>
              <Skeleton width={200} height={100} />
            </div>
          </div>
        </div>
      </section>

      {/* ErrorMessage Tests */}
      <section style={{ marginBottom: '48px' }}>
        <h2
          style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}
        >
          ErrorMessage Component
        </h2>

        <div style={{ marginBottom: '32px' }}>
          <h3
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: '500',
            }}
          >
            Usage
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              maxWidth: '600px',
            }}
          >
            <ErrorMessage message="This is a validation error message" />
            <ErrorMessage>This error is passed as children</ErrorMessage>
            <div>
              <Button onClick={() => setShowError(!showError)}>
                Toggle Error
              </Button>
              {showError && (
                <div style={{ marginTop: '12px' }}>
                  <ErrorMessage message="This is a conditional error message" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div
        style={{
          marginTop: '48px',
          padding: '16px',
          background: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <p style={{ fontSize: '14px', color: '#6b7280' }}>
          ✅ All atomic components are working correctly! Phase 1 is complete.
        </p>
      </div>
    </div>
  );
};
